import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast, Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PinButton from "@/components/PinButton";
import RecommendedProCard from "@/components/RecommendedProCard";
import MyBoardSidebar from "@/components/MyBoardSidebar";
import CategoryTypeahead from "@/components/CategoryTypeahead";
import { Search, BadgeCheck, Sparkles, Zap, Images, CheckCircle2, ArrowRight, MapPin, ChevronLeft, ChevronRight, X } from "lucide-react";

const PER_PAGE = 12;

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { workedWith, user } = useAuth();
  const [pros, setPros] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [freshPros, setFreshPros] = useState([]);
  const [meta, setMeta] = useState({ categories: [], cities: [] });
  const [boardVersion, setBoardVersion] = useState(0);
  const [catQuery, setCatQuery] = useState(params.get("category") || "");
  const [suggest, setSuggest] = useState(null); // { query, reason, sending }
  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    category: params.get("category") || "",
    city: params.get("city") || "",
    sort: "featured",
    verified: false,
    pro_plus: false,
    min_rating: 0,
  });

  useEffect(() => { api.get("/pros/categories").then(r => setMeta(r.data)); }, []);

  useEffect(() => {
    api.get("/pros?sort=newest&limit=60").then(r => {
      const eligible = r.data.filter(p => p.avatar_url && p.business_name);
      setFreshPros(eligible.slice(0, 3));
    });
  }, []);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [filters]);

  const _filterParams = () => {
    const q = new URLSearchParams();
    if (filters.q) q.set("q", filters.q);
    if (filters.category) q.set("category", filters.category);
    if (filters.city) q.set("city", filters.city);
    if (filters.verified) q.set("verified", "true");
    if (filters.pro_plus) q.set("pro_plus", "true");
    if (filters.min_rating) q.set("min_rating", filters.min_rating);
    return q;
  };

  useEffect(() => {
    const q = _filterParams();
    if (filters.sort) q.set("sort", filters.sort);
    q.set("limit", PER_PAGE);
    q.set("page", page);
    api.get(`/pros?${q.toString()}`).then(r => setPros(r.data));
    api.get(`/pros/count?${_filterParams().toString()}`).then(r => setTotal(r.data.total));
  }, [filters, page]);

  const pageCount = Math.max(1, Math.ceil(total / PER_PAGE));

  const goToPage = (p) => {
    setPage(p);
    document.getElementById("recommended-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const update = (patch) => setFilters({ ...filters, ...patch });
  const onPinChange = () => setBoardVersion(v => v + 1);
  const submitCategorySuggestion = async () => {
    if (!suggest) return;
    setSuggest(s => ({ ...s, sending: true }));
    try {
      await api.post("/categories/suggest", { suggested_category: suggest.query, reason: suggest.reason });
      toast.success("Thanks — sent to the LIMYA team for review.");
      setSuggest(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send suggestion");
      setSuggest(s => ({ ...s, sending: false }));
    }
  };

  return (
    <div>
      <Header/>
      <Toaster position="top-center"/>
      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* Filter bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 p-4 mb-4 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 flex-1 min-w-[220px] px-3 py-2 bg-black/5 rounded-xl">
            <Search size={16} className="text-[#222]/40"/>
            <input value={filters.q} onChange={e => update({ q: e.target.value })}
              placeholder="Search pros, services…"
              className="flex-1 outline-none bg-transparent text-sm"
              data-testid="search-query-input"/>
          </div>
          <select value={filters.city} onChange={e => update({ city: e.target.value })}
            className="px-3 py-2 bg-black/5 rounded-xl text-sm outline-none" data-testid="search-city-select">
            <option value="">Any city</option>
            {meta.cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="relative flex items-center gap-1">
            <CategoryTypeahead
              value={catQuery}
              onChange={setCatQuery}
              onSelect={(name) => { setCatQuery(name); update({ category: name }); setSuggest(null); }}
              onNotFound={(q) => setSuggest({ query: q, reason: "", sending: false })}
              placeholder="All services"
              testId="search-category-select"
              inputClassName="px-3 py-2 w-40 bg-black/5 rounded-xl text-sm outline-none"
            />
            {filters.category && (
              <button type="button" onClick={() => { setCatQuery(""); update({ category: "" }); }}
                className="absolute right-2 text-[#222]/40 hover:text-[#222]" data-testid="search-category-clear">
                <X size={14}/>
              </button>
            )}
          </div>
          <select value={filters.sort} onChange={e => update({ sort: e.target.value })}
            className="px-3 py-2 bg-black/5 rounded-xl text-sm outline-none" data-testid="search-sort-select">
            <option value="featured">Featured</option>
            <option value="rating">Highest rated</option>
            <option value="reviews">Most reviews</option>
            <option value="newest">Newest</option>
          </select>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="checkbox" checked={filters.verified} onChange={e => update({ verified: e.target.checked })} data-testid="filter-verified"/>
            <BadgeCheck size={14}/> Verified
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="checkbox" checked={filters.pro_plus} onChange={e => update({ pro_plus: e.target.checked })} data-testid="filter-pro-plus"/>
            Pro Plus
          </label>
        </div>

        {suggest && (
          <div className="bg-[#F2F4F2] border border-black/10 rounded-2xl p-3.5 mb-8 flex flex-wrap items-center gap-3" data-testid="search-suggest-category-panel">
            {user ? (
              <>
                <span className="text-sm text-[#222]/70">Can't find <b>&ldquo;{suggest.query}&rdquo;</b>?</span>
                <input value={suggest.reason} onChange={e => setSuggest(s => ({ ...s, reason: e.target.value }))}
                  placeholder="Why is this needed? (optional)"
                  className="px-3 py-1.5 border border-black/10 rounded-lg text-sm flex-1 min-w-[160px] outline-none focus:ring-2 focus:ring-[#2E6B57]"
                  data-testid="search-suggest-reason-input"/>
                <button onClick={submitCategorySuggestion} disabled={suggest.sending}
                  className="text-xs px-4 py-1.5 rounded-full bg-[#222] text-white font-semibold disabled:opacity-50 shrink-0" data-testid="search-suggest-submit">
                  {suggest.sending ? "Sending…" : "Send suggestion"}
                </button>
              </>
            ) : (
              <span className="text-sm text-[#222]/70">Can't find <b>&ldquo;{suggest.query}&rdquo;</b>? <Link to="/login" className="text-[#245544] font-semibold underline" data-testid="search-suggest-login-link">Sign in</Link> to suggest it.</span>
            )}
            <button onClick={() => setSuggest(null)} className="text-[#222]/40 hover:text-[#222] shrink-0" data-testid="search-suggest-dismiss"><X size={14}/></button>
          </div>
        )}

        {/* Two-column: main directory + My Board sidebar */}
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <div className="flex-1 min-w-0">

            {/* Fresh Faces on LIMYA */}
            {freshPros.length > 0 && (
              <section className="mb-10" data-testid="fresh-faces-section">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <h2 className="text-xl font-bold flex items-center gap-2" style={{fontFamily:'Outfit'}} data-testid="fresh-faces-heading">
                    Fresh Faces on LIMYA
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#E1EDE7] text-[#245544] text-[10px] font-bold tracking-wide">NEW</span>
                  </h2>
                  <button onClick={() => update({ sort: "newest" })} className="text-sm font-semibold text-[#245544] hover:underline inline-flex items-center gap-1 shrink-0" data-testid="fresh-faces-view-all">
                    View all new pros <ArrowRight size={14}/>
                  </button>
                </div>
                <p className="text-sm text-[#222]/60 mb-4">New local talent.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {freshPros.map(p => {
                    const badges = ["Fresh on LIMYA"];
                    if (p.business_status?.code === "accepting") badges.push("Accepting New Clients");
                    if (/hour/i.test(p.response_time || "")) badges.push("Responds Quickly");
                    if (p.cover_url || (p.services && p.services.length > 0)) badges.push("Portfolio Ready");
                    const badgeIcon = { "Fresh on LIMYA": Sparkles, "Accepting New Clients": CheckCircle2, "Responds Quickly": Zap, "Portfolio Ready": Images };
                    const badgeTone = {
                      "Fresh on LIMYA": "bg-[#EEF2EF] text-[#4E544D]",
                      "Accepting New Clients": "bg-[#E1EDE7] text-[#245544]",
                      "Responds Quickly": "bg-amber-100 text-amber-800",
                      "Portfolio Ready": "bg-[#C79B6B]/18 text-[#7a5a30]",
                    };
                    return (
                      <div key={p.user_id} className="relative bg-white rounded-2xl shadow-sm border border-black/5 hover:shadow-lg transition-all hover:-translate-y-1" data-testid={`fresh-card-${p.user_id}`}>
                        <div className="absolute top-2 right-2 z-10"><PinButton pro={p} variant="chip" onChange={onPinChange}/></div>
                        <button onClick={() => navigate(`/pro/${p.user_id}`)} className="block w-full text-left p-5">
                          <div className="flex items-center gap-3 mb-3">
                            <img src={p.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-[#F2F4F2]"/>
                            <div className="min-w-0">
                              <h3 className="font-bold truncate">{p.business_name}</h3>
                              <p className="text-xs text-[#222]/50 uppercase tracking-wider truncate">{p.category}{p.city ? ` · ${p.city}` : ""}</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {badges.map(b => {
                              const Icon = badgeIcon[b];
                              return (
                                <span key={b} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeTone[b] || "bg-[#EEF2EF] text-[#4E544D]"}`}>
                                  <Icon size={10}/> {b}
                                </span>
                              );
                            })}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Recommended pros */}
            <section data-testid="recommended-section">
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <h2 className="text-xl font-bold" style={{fontFamily:'Outfit'}} data-testid="recommended-heading">Recommended pros for you</h2>
                <span className="text-sm text-[#222]/55 shrink-0" data-testid="search-results-count">{total} pros</span>
              </div>
              <p className="text-sm text-[#222]/60 mb-5">Trusted local pros, matched to your search.</p>

              {pros.length === 0 ? (
                <div className="bg-white rounded-2xl border border-dashed border-black/15 p-12 text-center" data-testid="search-empty">
                  <MapPin size={28} className="mx-auto text-[#C79B6B] mb-3"/>
                  <h3 className="text-lg font-bold mb-1" style={{fontFamily:'Outfit'}}>No pros match your filters</h3>
                  <p className="text-sm text-[#222]/60">Try clearing a filter or searching a different service.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {pros.map(p => (
                      <RecommendedProCard key={p.user_id} pro={p} workedTogether={workedWith?.has(p.user_id)} onPinChange={onPinChange}/>
                    ))}
                  </div>

                  {/* Pagination */}
                  {pageCount > 1 && (
                    <div className="flex items-center justify-center gap-1.5 mt-10" data-testid="pagination">
                      <button
                        onClick={() => goToPage(page - 1)} disabled={page === 1}
                        className="w-9 h-9 grid place-items-center rounded-full border border-black/10 bg-white disabled:opacity-40 hover:bg-black/5 transition"
                        data-testid="page-prev"
                      ><ChevronLeft size={16}/></button>
                      {getPageWindow(page, pageCount).map((n, idx) =>
                        n === "…" ? (
                          <span key={`e${idx}`} className="w-9 h-9 grid place-items-center text-[#222]/40 text-sm">…</span>
                        ) : (
                          <button
                            key={n}
                            onClick={() => goToPage(n)}
                            className={`min-w-9 h-9 px-2 grid place-items-center rounded-full text-sm font-semibold transition ${
                              n === page ? "bg-[#222] text-white" : "border border-black/10 bg-white hover:bg-black/5"
                            }`}
                            data-testid={`page-${n}`}
                          >{n}</button>
                        )
                      )}
                      <button
                        onClick={() => goToPage(page + 1)} disabled={page === pageCount}
                        className="w-9 h-9 grid place-items-center rounded-full border border-black/10 bg-white disabled:opacity-40 hover:bg-black/5 transition"
                        data-testid="page-next"
                      ><ChevronRight size={16}/></button>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>

          {/* My Board sidebar */}
          <MyBoardSidebar version={boardVersion}/>
        </div>
      </div>
      <Footer />
    </div>
  );
}

// Windowed page numbers with ellipses: 1 … 4 5 [6] 7 8 … 20
function getPageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) out.push("…");
  for (let i = start; i <= end; i++) out.push(i);
  if (end < total - 1) out.push("…");
  out.push(total);
  return out;
}
